<template>
    <div class="grid md:grid-cols-3 gap-3" v-if="investorSummary">
        <k-panel class="border">
            <k-panel-header slot="header">
                <div slot="left">{{ $t("totalAssets") }}</div>
                <div slot="right">{{ investorSummary.totalAssets | currencyFormat(investorSummary.currencySymbol) }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="{content:'Formula: Total assets (=) Wallet (+) Committed (+) Investments'}"/>
                </div>
            </k-panel-header>
            <k-panel-row>
                <div slot="left">{{ $t("wallet") }}</div>
                <div slot="right">{{ investorSummary.wallet | currencyFormat(investorSummary.currencySymbol) }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="'Funds available in your account to be invested in debentures. Please check the Wallet section to learn how to transfer funds to your account.'"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("committed") }}</div>
                <div slot="right">{{ investorSummary.subscribed | currencyFormat(investorSummary.currencySymbol) }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="'Amount committed to the purchase of the debenture(s). All settlements are fulfilled via the Escrow Accounts of the Issuer and Investors in the Platform on a weekly basis.'"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("investments") }}</div>
                <div slot="right"> {{ investorSummary.investments  | currencyFormat(investorSummary.currencySymbol) }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="'Formula: Investments (=) Outstanding Principal (+) Pending Interest Payments. Outstanding Principal is the original sum of money put into investment(s) that have already been settled. Hence, the sum of the face value(s) of your Outstanding investment(s). The Pending Interest Payments is the sum of the scheduled interest payments that is in delay.'"/>
                </div>
            </k-panel-row>

            <div slot="footer">
                <a-button type="primary" @click.native.prevent="wallet()">Deposit funds</a-button>
            </div>
        </k-panel>
        <k-panel class="border">
            <k-panel-header slot="header">
                <div slot="left">{{ $t("netProfitability") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.averageProfitability : investorSummary.currentInvestments.averageProfitability | numeralFormat('0.00 %')
                    }}
                    <a-icon class="info-tooltip" type="info-circle" v-tooltip="$t('netProfitabilityDesc')"/>
                </div>
            </k-panel-header>
            <k-panel-row>
                <div slot="left">{{ $t("interestReceived") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.interestIncome : investorSummary.currentInvestments.interestIncome | currencyFormat(investorSummary.currencySymbol)
                    }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="$t('interestReceivedDesc')"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("interestForecast") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.outstandingInterestIncome : investorSummary.currentInvestments.outstandingInterestIncome | currencyFormat(investorSummary.currencySymbol)
                    }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="$t('interestForecastDesc')"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("feesExpense") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.feesExpense : investorSummary.currentInvestments.feesExpense | currencyFormat(investorSummary.currencySymbol)
                    }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="$t('feesExpenseDesc')"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("accruedInterestExpense") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.accruedInterestExpense : investorSummary.currentInvestments.accruedInterestExpense | currencyFormat(investorSummary.currencySymbol)
                    }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="$t('accruedInterestExpenseDesc')"/>
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("totalIncome") }}</div>
                <div slot="right">
                    {{
                        showLifetimeIncome ? investorSummary.lifetimeInvestments.totalIncome : investorSummary.currentInvestments.totalIncome | currencyFormat(investorSummary.currencySymbol)
                    }}
                    <a-icon class="info-tooltip" type="info-circle"
                            v-tooltip="$t('totalIncomeDesc')"/>
                </div>
            </k-panel-row>
            <div slot="footer">
                <a-checkbox
                    v-model="showLifetimeIncome"
                    id="lifetimeIncome"
                >
                    <span class="font-semibold">{{ $t("showLifetimeIncome") }}</span>
                </a-checkbox>
                <a-icon class="info-tooltip" type="info-circle"
                        v-tooltip="$t('showLifetimeIncomeDesc')"/>

            </div>
        </k-panel>
        <k-panel class="border">
            <k-panel-header slot="header">
                <div slot="left">{{ $t("investments") }}</div>
                <div slot="right">{{
                        investorSummary.investments | currencyFormat(investorSummary.currencySymbol)
                    }}
                </div>
            </k-panel-header>
            <k-panel-row>
                <div slot="left">{{ $t("current") }}</div>
                <div slot="right">{{
                        investorSummary.investmentsCurrent | currencyFormat(investorSummary.currencySymbol)
                    }}
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("past15") }}</div>
                <div slot="right">
                    {{ investorSummary.investmentsPastDue15 | currencyFormat(investorSummary.currencySymbol) }}
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("past30") }}</div>
                <div slot="right">
                    {{ investorSummary.investmentsPastDue30 | currencyFormat(investorSummary.currencySymbol) }}
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("past60") }}</div>
                <div slot="right">
                    {{ investorSummary.investmentsPastDue60 | currencyFormat(investorSummary.currencySymbol) }}
                </div>
            </k-panel-row>
            <k-panel-row>
                <div slot="left">{{ $t("past61") }}</div>
                <div slot="right">{{
                        investorSummary.investmentsDefault | currencyFormat(investorSummary.currencySymbol)
                    }}
                </div>
            </k-panel-row>
            <div slot="footer">
                <a-button type="primary" @click.native.prevent="invest()">{{ $t("invest") }}</a-button>
            </div>
        </k-panel>
    </div>

</template>

<script>
export default {
    props: {
        investorSummary: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showLifetimeIncome: false,
        };
    },
    methods: {
        invest() {
            this.$emit('invest');
        },
        wallet() {
            this.$emit('wallet');
            this.$trackGAEvent('investing', 'depositMoney');
        },
        formatCurrency(val) {
            if (!val) return val;
            return this.investorSummary.currencyCode + numeral(val).format('0,0.00')
        }
    }
};
</script>
<style>
.info-tooltip {
    vertical-align: 0.1em;
    margin-left: 5px;
}

.tooltip-inner {
    max-width: 450px;
    font-size: 14px;
}


</style>

